import { Box, Button } from 'core/components';
import { TextArea } from 'core/form';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { ENTER } from 'core/util/keyCodes';

import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import JourneysCopilotContextTags from './JourneysCopilotContextTags';

const Wrapper = styled(Box)`
  background: ${({ theme }) => (theme.name === 'light' ? theme.colors.cardBackground : '#252525')};
  padding: 12px;
  border-radius: 6px;
  width: 100%;
`;

const InputContainer = styled(Box)`
  position: relative;
  width: 100%;
`;

const WrappedInput = styled(TextArea)`
  width: 100%;
  border: none;
  outline: none;
  background: #fff;
  padding-top: 4px;
  padding-right: 50px;
  padding-bottom: 12px;
  padding-left: 9px;
  box-shadow: none;
  resize: none;
  position: relative;
  z-index: 1;
  font-size: 14px;
`;

const SendButton = styled(Button)`
  position: absolute;
  z-index: 2;
  top: 1px;
  right: -1px;
  background: rgba(45, 114, 210, 0.1);
  width: 32px !important;
  height: 32px !important;
  border-radius: 30px;
  padding: 0;

  .bp4-icon {
    width: 16px;
    height: 16px;
  }
`;

const JourneysCopilotInput = ({ value, onSubmit, activeChatContext, onRemoveContext }) => {
  const [inputValue, setInputValue] = React.useState(value);
  const [isFocused, setIsFocused] = React.useState(false);
  const [currentPlaceholderIndex] = React.useState(0);

  const contextPlaceholders = activeChatContext
    ? Array.from(activeChatContext)
        .map((item) => {
          if (item.placeholder) {
            return item.placeholder;
          }
          if (item.name) {
            return `Ask anything about ${item.name}`;
          }
          return null;
        })
        .filter(Boolean)
    : [];

  const defaultPlaceholder = 'Ask any question about your network';

  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
    setIsFocused(true);
  }, [value]);

  const handleSubmit = () => {
    if (inputValue?.trim()) {
      if (onSubmit) {
        onSubmit(inputValue);
      }

      setInputValue('');
    }
  };

  const onKeyDown = (e) => {
    const key = e.keyCode ? e.keyCode : e.which;
    const isShiftPressed = e.shiftKey;
    const hasValue = inputValue?.length > 0;

    // user presses ENTER without shift, and the input is empty, don't submit, don't make a new line
    if (key === ENTER && !isShiftPressed && !hasValue) {
      e.preventDefault();
      return;
    }

    // user presses ENTER with shift, add a new line
    if (key === ENTER && isShiftPressed) {
      return;
    }

    // - shift + enter does not submit, it makes a new line
    // - enter without shift submits
    // - this is the same behavior as most common apps, ChatGPT, Slack, etc.
    if (key === ENTER && !isShiftPressed && hasValue) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Get current placeholder
  const getCurrentPlaceholder = () => {
    if (contextPlaceholders.length === 0) {
      return defaultPlaceholder;
    }
    return `${contextPlaceholders[currentPlaceholderIndex]}`;
  };

  return (
    <Wrapper
      className={classNames({ 'bp4-elevation-2': isFocused })}
      isFocused={isFocused}
      border={isFocused ? 'active' : 'thin'}
    >
      <InputContainer>
        <JourneysCopilotContextTags activeChatContext={activeChatContext} onRemoveContext={onRemoveContext} />
        <WrappedInput
          fill
          autoFocus
          autoGrow
          placeholder={getCurrentPlaceholder()}
          rows={1}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          onChange={(e) => {
            setInputValue(e.target.value);
          }}
          value={inputValue}
          onKeyDown={onKeyDown}
        />

        {inputValue?.trim() && (
          <SendButton onClick={handleSubmit} intent="primary" icon="send-message" minimal ariaLabel="Ask AI Advisor" />
        )}
      </InputContainer>
    </Wrapper>
  );
};

export default withRouter(observer(JourneysCopilotInput));
